import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled", "id", "indeterminate", "inputRef", "invalid", "labelProps", "labelRef", "onChange", "required", "size", "visualizeRequiredCheckbox", "wrapperProps", "wrapperRef"];
import classNames from 'classnames';
import React, { useState, useContext, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCombinedRefs } from '@op/opds.opux';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
import { CheckboxGroupContext } from '../checkbox-group/checkbox-group';
import { FormContext } from '../form/form';
import { FormRowContext } from '../form-row/form-row';
/**
 * _An option that can be toggled on or off._
 *
 * ***
 *
 * A checkbox. Additional props will be forwarded to the `<input>` element.
 */
const Checkbox = _ref => {
  let {
      children,
      disabled: propDisabled,
      id: propId,
      indeterminate: propIndeterminate = false,
      inputRef: propInputRef,
      invalid: propInvalid = false,
      labelProps = {},
      labelRef,
      onChange: _onChange,
      required: propRequired,
      size: propSize,
      visualizeRequiredCheckbox = false,
      wrapperProps = {},
      wrapperRef
    } = _ref,
    inputProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    spacing,
    visualizeRequiredFields: formVisualizeRequired
  } = useContext(FormContext);
  const {
    disabled: rowDisabled,
    isInsideFormRow,
    required: contextRequired
  } = useContext(FormRowContext);
  const {
    buttons,
    columns,
    disabled: groupDisabled,
    invalid: groupInvalid,
    size: groupSize,
    wrap
  } = useContext(CheckboxGroupContext);
  const internalInputRef = useRef();
  const inputRef = useCombinedRefs(propInputRef, internalInputRef);
  const [stateId] = useState(() => "ds-cb-" + uuidv4());
  const required = propRequired != null ? propRequired : contextRequired;
  const visualizeRequired = !isInsideFormRow && (formVisualizeRequired || visualizeRequiredCheckbox) && required;
  const disabled = propDisabled || groupDisabled || rowDisabled;
  const indeterminate = !inputProps.checked && propIndeterminate;
  const invalid = propInvalid || groupInvalid;
  const size = propSize || groupSize || spacing;
  const id = propId != null ? propId : stateId;
  const cn = buildVersionedClassNames('ds-checkbox', wrapperProps.className, {
    // Tiny variant is meant only for tight contexts like tables,
    // using it within a bordered button is not supported.
    'ds-checkbox--buttonized': buttons && size !== 'tiny',
    'ds-checkbox--disabled': disabled,
    'ds-checkbox--invalid': invalid,
    ["ds-checkbox--" + size]: size !== 'default'
  });
  const lcn = classNames(buildVersionedClass('ds-checkbox__label'), labelProps.className, {
    [buildVersionedClass('ds-checkbox__label--visualize-required')]: visualizeRequired
  });
  const width = columns > 1 && !wrap ? 100 / columns + "%" : undefined;
  useEffect(() => {
    if (internalInputRef.current) {
      internalInputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, internalInputRef]);
  return /*#__PURE__*/React.createElement("div", _extends({}, wrapperProps, {
    className: cn,
    ref: wrapperRef,
    style: _extends({
      width
    }, wrapperProps.style)
  }), /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    "aria-invalid": invalid,
    disabled: disabled,
    id: id,
    onChange: ev => {
      if (_onChange) {
        _onChange(ev, ev.target.checked);
      }
    },
    ref: inputRef,
    required: required,
    type: "checkbox"
  })), /*#__PURE__*/React.createElement("label", _extends({}, labelProps, {
    className: lcn,
    htmlFor: id,
    ref: labelRef
  }), children));
};
Checkbox.displayName = 'Checkbox';
export default Checkbox;